import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { RootState, StoreError } from "store/reducer";
import { IdResourceSelectors } from "store/util/id-resource";

/**
 * Replaces the boilerplate every event-scoped screen used to roll by hand:
 *
 *   useEffect(() => { dispatch(load(id)); }, [dispatch, id]);
 *   const item = useSelector(s => s.slice.items.find(...));
 *   const loading = useSelector(s => s.slice.itemsLoading[id]);
 *   const error = useSelector(s => s.slice.itemsError[id]);
 *   const showLoading = !item && !error && (loading || loadedFor !== id);
 *
 * Returns `{ item, loading, error, showLoading }`. `showLoading` is the
 * single derived flag screens should render against — true while a fetch is
 * in flight AND there's nothing to show yet.
 */
export type UseIdResourceArgs<T> = {
  id: string;
  selectors: IdResourceSelectors<RootState, T>;
  load: (id: string) => AnyAction | ((...args: any[]) => any);
};

export type UseIdResourceResult<T> = {
  item: T | undefined;
  loading: boolean;
  error: StoreError | null;
  showLoading: boolean;
};

const useIdResource = <T,>({
  id,
  selectors,
  load,
}: UseIdResourceArgs<T>): UseIdResourceResult<T> => {
  const dispatch = useDispatch<ThunkDispatch<RootState, unknown, AnyAction>>();
  const item = useSelector(selectors.selectItem(id));
  const loading = useSelector(selectors.selectLoading(id));
  const error = useSelector(selectors.selectError(id));

  useEffect(() => {
    if (!id) return;
    dispatch(load(id) as AnyAction);
  }, [dispatch, id, load]);

  return {
    item,
    loading,
    error,
    showLoading: !item && !error && loading,
  };
};

export default useIdResource;
